import { commitMutation, useLazyLoadQuery } from 'react-relay';
import { Redirect, usePathParam } from 'react-resource-router';
import { ATLASSIAN_ACCOUNT_USER_QUERY } from 'constants/index';
import { environment } from 'services/relay';
import { AtlassianAccountUserQuery } from 'graphql/__generated__/AtlassianAccountUserQuery.graphql';
import { COMPLETE_TRANSACTION_MUTATION } from 'graphql/CompleteTransactionMutation';
import {
  CompleteTransactionMutation,
  CompleteTransactionMutation$variables,
} from 'graphql/__generated__/CompleteTransactionMutation.graphql';
import { useEffect, useState } from 'react';
import Loader from 'components/common/loader/Loader';
import {
  trackAccountLinkingSuccess,
  trackAccountLinkingFailure,
} from 'analytics/settingsAnalytics';
import useStorageTimeStamp from 'hooks/useStorageTimeStamp';

const completeTransaction = (
  input: CompleteTransactionMutation$variables,
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    commitMutation<CompleteTransactionMutation>(environment, {
      mutation: COMPLETE_TRANSACTION_MUTATION,
      variables: input,
      onCompleted: (response, errors) => {
        const authenticateTransactionVal =
          response?.unified?.linking?.completeTransaction;

        if (authenticateTransactionVal?.success === false || errors) {
          let errorCode = 'generic_error';
          if (errors) {
            console.error('Initialization of transaction failed', errors);
          } else {
            errorCode =
              authenticateTransactionVal?.errors?.[0]?.code || 'generic_error';
          }
          reject({
            errorCode,
          });
        } else {
          resolve(authenticateTransactionVal?.success ?? false);
        }
      },
      onError: (error) => {
        console.error('Initialization of transaction failed', error);
        reject({
          errorCode: 'generic_error',
        });
      },
    });
  });
};

const Complete = () => {
  const [encryptedToken] = usePathParam('token');

  const [isCompleted, setIsCompleted] = useState<boolean | null>(null);
  const { calculateTimeDiff } = useStorageTimeStamp();
  if (!encryptedToken) {
    return <Redirect to={'/error/invalid_path'} />;
  }
  const token = decodeURIComponent(encryptedToken);
  const loggedInUser = useLazyLoadQuery<AtlassianAccountUserQuery>(
    ATLASSIAN_ACCOUNT_USER_QUERY,
    {},
    { fetchPolicy: 'store-or-network' },
  )?.me?.user;

  if (!loggedInUser?.accountId) {
    return <Redirect to={'/error/not_authorized'} />;
  }

  useEffect(() => {
    (async () => {
      try {
        const status = await completeTransaction({ token });
        if (status) {
          const duration = calculateTimeDiff();
          trackAccountLinkingSuccess({ duration });

          setIsCompleted(true);
        } else {
          throw {
            errorCode: 'generic_error',
          };
        }
      } catch (e: any) {
        console.error('Failed to complete transaction', e);
        const errorCodeReceived = e?.errorCode || 'generic_error';
        const duration = calculateTimeDiff();

        trackAccountLinkingFailure(errorCodeReceived, 'completion', {
          duration,
        });

        window.open(`/user/settings?errorCode=${errorCodeReceived}`, '_self');
      }
    })();
  }, []);

  if (isCompleted) {
    return <Redirect to={`/settings`} />;
  } else if (isCompleted === false) {
    return <Redirect to={'/error'} />;
  } else {
    return <Loader />;
  }
};

export default Complete;
